import type { ExternalMotionSearchResultItem } from '@cartwhl/common/api/postExternalMotionSearch';

import type { AssetLibraryFilters } from './assetLibraryFilters';

export const MOTION_LIBRARY_PAGE_SIZE = 12;
export const MOTION_LIBRARY_SEARCH_DEBOUNCE_MS = 320;
export const MOTION_LIBRARY_LOW_ASSET_SUPPLEMENT_THRESHOLD = 8;

export type MotionLibraryDisplayItem = {
  motionID: string;
  title: string;
  previewURL: string;
  short: string;
};

export type MotionLibraryLoadMode = 'none' | 'fallback' | 'supplement';

export type MotionLibraryLoadState = {
  status: 'initial' | 'pending' | 'ready' | 'error';
  items: MotionLibraryDisplayItem[];
  hasMore: boolean;
  lastSort?: [number, string];
  error?: string;
  isLoadingMore: boolean;
};

export const INITIAL_MOTION_LIBRARY_LOAD_STATE: MotionLibraryLoadState = {
  status: 'initial',
  items: [],
  hasMore: false,
  isLoadingMore: false,
};

export function getMotionLibraryLoadMode(input: {
  filters: AssetLibraryFilters;
  assetCount: number;
  displayAssetCount: number;
  assetsStatus: 'initial' | 'pending' | 'ready' | 'error';
  assetsExhausted: boolean;
  disableMotionLibrary?: boolean;
}): MotionLibraryLoadMode {
  if (input.disableMotionLibrary) {
    return 'none';
  }

  if (
    input.filters.typeFilter === 'image' ||
    input.filters.typeFilter === 'video'
  ) {
    return 'none';
  }

  const searchTerm = input.filters.searchQuery.trim();

  if (!searchTerm) {
    if (input.assetsStatus !== 'ready') {
      return 'none';
    }

    if (input.displayAssetCount === 0) {
      return input.assetsExhausted ? 'fallback' : 'none';
    }

    if (
      input.displayAssetCount <= MOTION_LIBRARY_LOW_ASSET_SUPPLEMENT_THRESHOLD
    ) {
      return 'supplement';
    }

    return 'none';
  }

  if (input.assetsStatus !== 'ready') {
    return 'none';
  }

  if (input.displayAssetCount === 0) {
    return 'fallback';
  }

  if (input.assetsExhausted) {
    return 'supplement';
  }

  return 'none';
}

export function toMotionLibraryDisplayItem(
  item: ExternalMotionSearchResultItem,
): MotionLibraryDisplayItem {
  return {
    motionID: item.motionID,
    title: item.title?.trim() || item.motionID,
    previewURL: item.mp4URL || item.gifURL,
    short: item.short?.trim() ?? '',
  };
}

export function getMotionLibraryGridItemKey(item: MotionLibraryDisplayItem): string {
  return `motion-library:${item.motionID}`;
}
